<template>
  <modal-view
    title="Initialize EBM"
    :custom-max-width="'600px'"
    @close-modal="$emit('close')"
  >
    <div class="px-6 pb-8 space-y-5">
      <div class="space-y-3">
        <!-- Force Re-initialization Warning - Only show if EBM is already initialized -->
        <div
          v-if="isEbmInitialized"
          class="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm dark:border-red-800 dark:bg-red-900/30"
        >
          <div class="flex items-start">
            <i
              class="fas fa-exclamation-triangle text-red-600 dark:text-red-400 mr-2 mt-0.5"
            ></i>
            <div>
              <p
                class="font-semibold text-red-800 dark:text-red-200 mb-2"
              >
                ⚠️ Critical Warning: Force Re-initialization May Break EBM
              </p>
              <div class="space-y-2 text-red-700 dark:text-red-300">
                <p>
                  <strong>This store's EBM is already initialized.</strong> Force re-initialization will bypass all safety checks and may cause serious issues including:
                </p>
                <ul class="list-disc list-inside ml-2 space-y-1">
                  <li>Loss of invoice number continuity</li>
                  <li>Disruption of active EBM operations</li>
                  <li>Potential data synchronization issues with RRA</li>
                  <li>Possible EBM system malfunction</li>
                </ul>
                <p class="mt-2 font-semibold">
                  ⚠️ Only proceed if:
                </p>
                <ul class="list-disc list-inside ml-2 space-y-1">
                  <li>You have been explicitly instructed by <strong>RRA EBM support staff</strong> to re-initialize</li>
                  <li>You fully understand the consequences and know what you are doing</li>
                  <li>You have a valid reason that cannot be resolved through normal channels</li>
                </ul>
                <p class="mt-2 font-semibold italic">
                  This action cannot be undone and may require RRA intervention to fix.
              </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-900/40"
        >
          <p class="font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Store Information
          </p>
          <div class="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <p>
              <span class="font-medium">Store:</span>
              {{ storeName || `Store #${storeId}` }}
            </p>

            <!-- Basic EBM Settings -->
            <div
              v-if="ebmSettings"
              class="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600"
            >
              <p class="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                EBM Configuration
              </p>
              <div class="space-y-1">
                <p v-if="ebmSettings.tin">
                  <span class="font-medium">TIN:</span> {{ ebmSettings.tin }}
                </p>
                <p v-if="ebmSettings.bhfId || ebmSettings.bhf_id">
                  <span class="font-medium">Branch ID:</span>
                  {{ ebmSettings.bhfId || ebmSettings.bhf_id }}
                </p>
                <p v-if="ebmSettings.dvcSrlNo">
                  <span class="font-medium">Device Serial No:</span>
                  {{ ebmSettings.dvcSrlNo }}
                </p>
                <p v-if="ebmSettings.mrc">
                  <span class="font-medium">MRC:</span> {{ ebmSettings.mrc }}
                </p>
                <p
                  v-if="ebmSettings.ebm_initialized"
                  class="text-emerald-600 dark:text-emerald-400"
                >
                  <span class="font-medium">Status:</span> Currently Initialized
                </p>
                <p v-else class="text-gray-600 dark:text-gray-400">
                  <span class="font-medium">Status:</span> Not Initialized
                </p>
                <p v-if="ebmSettings.ebm_activated !== undefined">
                  <span class="font-medium">EBM Activated:</span>
                  <span
                    :class="
                      ebmSettings.ebm_activated
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-600 dark:text-gray-400'
                    "
                  >
                    {{ ebmSettings.ebm_activated ? "Yes" : "No" }}
                  </span>
                </p>
                <p v-if="ebmSettings.vat_activated !== undefined">
                  <span class="font-medium">VAT Activated:</span>
                  <span
                    :class="
                      ebmSettings.vat_activated
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-600 dark:text-gray-400'
                    "
                  >
                    {{ ebmSettings.vat_activated ? "Yes" : "No" }}
                  </span>
                </p>
                <p v-if="ebmSettings.ebm_initialized_at">
                  <span class="font-medium">Initialized At:</span>
                  {{ formatDate(ebmSettings.ebm_initialized_at) }}
                </p>
              </div>
            </div>

            <!-- Initialization Info (if available) -->
            <div
              v-if="ebmSettings?.initialization_info"
              class="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600"
            >
              <p class="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Initialization Details
              </p>
              <div class="space-y-1">
                <p v-if="ebmSettings.initialization_info.bhfNm">
                  <span class="font-medium">Branch Name:</span>
                  {{ ebmSettings.initialization_info.bhfNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.bhfOpenDt">
                  <span class="font-medium">Branch Open Date:</span>
                  {{ ebmSettings.initialization_info.bhfOpenDt }}
                </p>
                <p v-if="ebmSettings.initialization_info.bsnsActv">
                  <span class="font-medium">Business Activity:</span>
                  {{ ebmSettings.initialization_info.bsnsActv }}
                </p>
                <p v-if="ebmSettings.initialization_info.dstrtNm">
                  <span class="font-medium">District Name:</span>
                  {{ ebmSettings.initialization_info.dstrtNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.prvncNm">
                  <span class="font-medium">Province Name:</span>
                  {{ ebmSettings.initialization_info.prvncNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.sctrNm">
                  <span class="font-medium">Sector Name:</span>
                  {{ ebmSettings.initialization_info.sctrNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.taxprNm">
                  <span class="font-medium">Taxpayer Name:</span>
                  {{ ebmSettings.initialization_info.taxprNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.mgrNm">
                  <span class="font-medium">Manager Name:</span>
                  {{ ebmSettings.initialization_info.mgrNm }}
                </p>
                <p v-if="ebmSettings.initialization_info.mgrEmail">
                  <span class="font-medium">Manager Email:</span>
                  {{ ebmSettings.initialization_info.mgrEmail }}
                </p>
                <p v-if="ebmSettings.initialization_info.mgrTelNo">
                  <span class="font-medium">Manager Tel:</span>
                  {{ ebmSettings.initialization_info.mgrTelNo }}
                </p>
                <p v-if="ebmSettings.initialization_info.hqYn">
                  <span class="font-medium">Headquarters:</span>
                  {{
                    ebmSettings.initialization_info.hqYn === "Y" ? "Yes" : "No"
                  }}
                </p>
                <p v-if="ebmSettings.initialization_info.vatTyCd">
                  <span class="font-medium">VAT Type Code:</span>
                  {{ ebmSettings.initialization_info.vatTyCd }}
                </p>
              </div>
            </div>

            <!-- Invoice Numbers (if available) -->
            <div
              v-if="
                ebmSettings?.initialization_info &&
                (ebmSettings.initialization_info.lastInvcNo ||
                  ebmSettings.initialization_info.lastSaleInvcNo ||
                  ebmSettings.initialization_info.lastPchsInvcNo)
              "
              class="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600"
            >
              <p class="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Invoice Numbers
              </p>
              <div class="space-y-1">
                <p v-if="ebmSettings.initialization_info.lastInvcNo">
                  <span class="font-medium">Last Invoice No:</span>
                  {{ ebmSettings.initialization_info.lastInvcNo }}
                </p>
                <p v-if="ebmSettings.initialization_info.lastSaleInvcNo">
                  <span class="font-medium">Last Sale Invoice No:</span>
                  {{ ebmSettings.initialization_info.lastSaleInvcNo }}
                </p>
                <p v-if="ebmSettings.initialization_info.lastPchsInvcNo">
                  <span class="font-medium">Last Purchase Invoice No:</span>
                  {{ ebmSettings.initialization_info.lastPchsInvcNo }}
                </p>
                <p v-if="ebmSettings.initialization_info.lastCopyInvcNo">
                  <span class="font-medium">Last Copy Invoice No:</span>
                  {{ ebmSettings.initialization_info.lastCopyInvcNo }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Force checkbox - Only show if EBM is already initialized -->
      <div v-if="isEbmInitialized" class="flex items-start">
        <input
          id="force-confirm"
          v-model="forceConfirmed"
          type="checkbox"
          class="mt-1 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500 dark:border-red-600 dark:bg-gray-800"
        />
        <label
          for="force-confirm"
          class="ml-3 text-sm text-red-700 dark:text-red-300"
        >
          <span class="font-semibold">I understand the risks</span> and confirm that:
          <ul class="list-disc list-inside ml-4 mt-1 space-y-1">
            <li>I have been instructed by <strong>RRA EBM support staff</strong> to perform this re-initialization, OR</li>
            <li>I fully understand that this may <strong>break the EBM system</strong> and I know what I am doing</li>
            <li>This action will force re-initialize the EBM system, bypassing all safety checks</li>
            <li>This action <strong>cannot be undone</strong> and may require RRA intervention to resolve issues</li>
          </ul>
        </label>
      </div>

      <!-- Success Message -->
      <div
        v-if="successMessage"
        class="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
      >
        <div class="flex items-start">
          <i class="fas fa-check-circle mr-2 mt-0.5"></i>
          <div>
            <p class="font-semibold mb-1">Initialization Successful</p>
            <p>{{ successMessage }}</p>
            <div v-if="responseDetails" class="mt-2 text-xs opacity-90">
              <p v-if="responseDetails.resultCd">
                <span class="font-medium">Result Code:</span>
                {{ responseDetails.resultCd }}
              </p>
              <p v-if="responseDetails.resultMsg">
                <span class="font-medium">Message:</span>
                {{ responseDetails.resultMsg }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
      >
        <div class="flex items-start">
          <i class="fas fa-exclamation-circle mr-2 mt-0.5"></i>
          <div class="flex-1">
            <p class="font-semibold mb-1">Error</p>
            <p>{{ error }}</p>
            <div v-if="errorDetails" class="mt-2 text-xs opacity-90 space-y-1">
              <p v-if="errorDetails.statusCode">
                <span class="font-medium">Status Code:</span>
                {{ errorDetails.statusCode }}
              </p>
              <p v-if="errorDetails.resultCd">
                <span class="font-medium">RRA Result Code:</span>
                {{ errorDetails.resultCd }}
              </p>
              <p v-if="errorDetails.resultMsg">
                <span class="font-medium">RRA Message:</span>
                {{ errorDetails.resultMsg }}
              </p>
              <p v-if="errorDetails.details" class="mt-1">
                {{ errorDetails.details }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Message -->
      <div
        v-if="loading && loadingMessage"
        class="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
      >
        <div class="flex items-center">
          <i class="fas fa-sync fa-spin mr-2"></i>
          <p>{{ loadingMessage }}</p>
        </div>
      </div>

      <div class="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          :disabled="loading"
          @click="$emit('close')"
        >
          {{ successMessage ? "Close" : "Cancel" }}
        </button>
        <button
          v-if="!successMessage && ebmSettings && !error"
          type="button"
          :disabled="(isEbmInitialized && !forceConfirmed) || loading"
          class="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
          @click="handleConfirm"
        >
          <svg
            v-if="loading"
            class="mr-2 h-4 w-4 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          {{ isEbmInitialized ? "Force Initialize EBM" : "Initialize EBM" }}
        </button>
      </div>
    </div>
  </modal-view>
</template>

<script>
import { ref, computed } from "vue";
import ModalView from "@/components/modals/ModalView.vue";

export default {
  name: "EbmInitializeModal",
  components: {
    ModalView,
  },
  props: {
    storeId: {
      type: Number,
      required: true,
    },
    storeName: {
      type: String,
      default: "",
    },
    ebmSettings: {
      type: Object,
      default: () => null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingMessage: {
      type: String,
      default: "",
    },
    error: {
      type: String,
      default: "",
    },
    errorDetails: {
      type: Object,
      default: () => null,
    },
    successMessage: {
      type: String,
      default: "",
    },
    responseDetails: {
      type: Object,
      default: () => null,
    },
  },
  emits: ["close", "confirm"],
  setup(props, { emit }) {
    const forceConfirmed = ref(false);

    const isEbmInitialized = computed(() => {
      return props.ebmSettings?.ebm_initialized === true;
    });

    const formatDate = (dateString) => {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch (e) {
        return dateString;
      }
    };

    const handleConfirm = () => {
      // If EBM is initialized, require force confirmation
      if (isEbmInitialized.value && !forceConfirmed.value) {
        return;
      }
      emit("confirm", {
        storeId: props.storeId,
        force: isEbmInitialized.value && forceConfirmed.value,
      });
    };

    return {
      forceConfirmed,
      isEbmInitialized,
      handleConfirm,
      formatDate,
    };
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
